// Resolves an audit "doer" to a human-readable actor label.
//
// One value, two homes: tio-api writes the same snapshot to the `created_by`/`updated_by`
// jsonb columns (via InjectContext) and to PaperTrail's `whodunnit`, so both are read here.
//
// It comes in a few shapes:
//   - a structured person actor — someone acting through the API:
//       { user: { first_name: 'Fidel', last_name: 'Lee', id: '218839' }, type: 'person', ... }
//   - a system/service actor — a ServiceUser (jobs/consumers) or a TokenUser on the root user;
//     carries `type: 'system'` and no person name, plus an optional `service.service_name`:
//       { user: { id: '1' }, type: 'system', service: { service_name: 'TasApplicationAutoRejectionJob' } }
//   - a bare string — a background worker/job context that set the whodunnit directly, e.g.
//       'worker-EligibilityFilesImportJob::EligibilityFileProcessorJob'
//   - a bare number — a legacy integer doer column, which carries no name at all
//   - null / blank — no recorded actor
//
// Every subfield is built through `.to_s` on the API side, so an absent value arrives as an
// empty string rather than a missing key — test them for truthiness only.
//
// Returns '' when there is no identifiable actor, so callers can localize the fallback
// (e.g. 'system').
export interface AuditDoerHash {
  user?: { first_name?: string; last_name?: string; id?: string; person_id?: string };
  type?: 'person' | 'system';
  service?: { service_name?: string } | null;
  api?: { id?: string; key?: string; user?: string };
  impersonator?: { first_name?: string; last_name?: string; id?: string };
  ts?: string;
  trace_id?: string;
}

export type AuditDoer = AuditDoerHash | string | number | null | undefined;

// A snapshot is self-describing: '' from one means "no identifiable actor" and callers may
// safely say so. '' from a scalar id means only that the lookup failed, which is NOT the
// same claim — so callers that localize a "system" fallback must gate it on this.
export function isAuditDoerHash(value: unknown): value is AuditDoerHash {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function actorName(doer: AuditDoer): string {
  if (!doer) return '';

  // Worker/job contexts are recorded as a raw string; drop the noisy 'worker-' prefix.
  if (typeof doer === 'string') return doer.replace(/^worker-/, '');

  // A legacy integer doer column is a bare user id — an id is not a name.
  if (typeof doer === 'number') return '';

  // System/service actors (a ServiceUser, or a TokenUser on the root user) carry type:'system'
  // and no person name. Prefer the originating service/job name; otherwise return '' so the
  // caller localizes the 'system' fallback.
  if (doer.type === 'system') return doer.service?.service_name ?? '';

  const user = doer.user;
  if (!user) return '';

  const name = [user.first_name, user.last_name].filter(Boolean).join(' ');
  return name || (user.id ? `user ${user.id}` : '');
}
